import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { GetCoinPropertiesResponse } from "deso-protocol";
import { getCoinProperties } from "deso-protocol";

import type { BaseCurrencyPriceEntry } from "@/backend/deso";
import { getBaseCurrencyPrice } from "@/backend/deso";

type UseCoinPropertiesQueryProps = {
  publicKey: string;
};

export type CoinPropertiesResponse = {
  coinProperties: GetCoinPropertiesResponse;
  coinPrice: BaseCurrencyPriceEntry | null;
  ammRunning: boolean;
  hasCoin: boolean;
  hasAmmConfigs: boolean;
};

type UseCoinPropertiesQueryOptions = Omit<
  UseQueryOptions<CoinPropertiesResponse | null>,
  "queryKey" | "queryFn"
>;

const DISABLED_TOKEN_PUBLIC_KEYS = [
  "BC1YLgVpgunEsaz6ntRWYpd1k72HskyoiUpWBURx7iDgrDubpGXLERJ", // ZenithVulture1
  "BC1YLjMvaojbzhDXtKjVZr5q4DFNfZAx58UuYVfsMe6y65T6h891buN", // EnigmaticZebu72
  "BC1YLjBKrvB2x8qF39X8Udvmgoj6NTZP8cVqx5BPV5PvtRXn9xLcFhm", // HarmonicJaguar54
  "BC1YLjYNokuuCCLyZ1jiDa4xtff2bZMtTB9m7hUMfw9n3iMWSx2WYdE", // MysteriousSquirrel1
  "BC1YLhUXbQkXmfivxZLoWh2ZBXuhxmc6JkmmU5hDbWqj9rvN5T678C4", // SplendiferousPenguin2
  "BC1YLiM5YbEDf5wgeryLicE53TpzoUxVJLE23T72NteS7qvRywwdvWH", // VelvetyWalrus61
  "BC1YLh1MLd3v1xkCxscqfWYpet3sFfkQ4jY6iBN7prvbz1cgwAUe6Ks", // SimpleNaders
];

const emptyCoinPropertiesResponse: GetCoinPropertiesResponse = {
  AmmConfigs: [],
  RunAt: [],
  Levels: [],
  OrdersPlaced: [],
  Profile: {
    PublicKeyBase58Check: "",
    Username: "",
    Description: "",
    IsHidden: false,
    IsReserved: false,
    IsVerified: false,
    Comments: null,
    Posts: null,
    CoinEntry: {
      CreatorBasisPoints: 0,
      DeSoLockedNanos: 30405011137,
      NumberOfHolders: 29,
      CoinsInCirculationNanos: 31211520038,
      CoinWatermarkNanos: 77988000500,
      BitCloutLockedNanos: 30405011137,
    },
    DAOCoinEntry: {
      NumberOfHolders: 0,
      CoinsInCirculationNanos: [],
      MintingDisabled: false,
      TransferRestrictionStatus: "unrestricted",
    },
    CoinPriceDeSoNanos: 2922479982,
    CoinPriceBitCloutNanos: 2922479982,
    UsersThatHODL: null,
    IsFeaturedTutorialWellKnownCreator: false,
    IsFeaturedTutorialUpAndComingCreator: false,
    ExtraData: {},
    DESOBalanceNanos: 0,
    BestExchangeRateDESOPerDAOCoin: 0,
  },
  CreatorRevsharePercentageBasisPoints: 0,
  TradingFeeMap: {},
  TotalTradingFeeBasisPoints: 0,
  FeeProfiles: {},
  CoinApyBasisPoints: 0,
  MinLockupDurationNanos: 9223372036854776000,
  YieldCurvePoints: [],
  QuoteCurrencyInAmmBids: [],
  UsdInAmmBids: [],
  CoinsInAmmAsks: [],
  CoinsInAmmPubkeys: [],
  QuoteCurrencyInAmmBidsTotal: 0,
  UsdInAmmBidsTotal: 0,
  CoinsInAmmAsksTotal: 0,
  CoinsInAmmPubkeysTotal: 0,
  DisableCreatorRevshareUpdate: false,
  DisableMintingOfNewCoins: false,
  DisableTradingFeeUpdate: false,
  EnablePermanentlyUnrestrictedTransfers: false,
} as unknown as GetCoinPropertiesResponse;

export const useCoinPropertiesQuery = (
  { publicKey }: UseCoinPropertiesQueryProps,
  options?: UseCoinPropertiesQueryOptions,
) => {
  return useQuery({
    queryKey: ["coinProperties", publicKey],
    queryFn: async () => {
      if (!publicKey) {
        return null;
      }

      const [coinProperties, coinPrice] = await Promise.all([
        DISABLED_TOKEN_PUBLIC_KEYS.includes(publicKey)
          ? Promise.resolve(emptyCoinPropertiesResponse)
          : getCoinProperties(
              {
                BaseCurrencyPublicKey: publicKey,
              },
              {
                nodeURI: process.env.NEXT_PUBLIC_AMM_URI,
              },
            ),
        getBaseCurrencyPrice([
          {
            BaseCurrencyPublicKeyBase58Check: publicKey,
            BaseCurrencyQuantityToSell: 1,
          },
        ]),
      ]);

      const hasAmmConfigs = Boolean(coinProperties?.AmmConfigs?.length);
      const coinRunAtDate = coinProperties?.RunAt?.[0];

      const ammRunning = coinRunAtDate
        ? dayjs(coinRunAtDate).isBefore(dayjs())
        : hasAmmConfigs;

      // TODO: fix type for CoinsInCirculationNanos
      const hasCoin =
        parseInt(
          (coinProperties?.Profile?.DAOCoinEntry
            ?.CoinsInCirculationNanos as unknown as string) || "0x0",
          16,
        ) > 0;

      return {
        coinProperties,
        coinPrice: hasAmmConfigs || hasCoin ? (coinPrice?.[0] ?? null) : null,
        ammRunning,
        hasAmmConfigs,
        hasCoin,
      };
    },
    // 10 sec caching to prevent multiple requests on a single page load
    staleTime: 1000 * 10,
    ...options,
  });
};
